import { GIFEncoder, quantize, applyPalette } from 'gifenc';
import { parseGIF, decompressFrames } from 'gifuct-js';
import { formatFileSize } from '../compress/compressImageEngine';

export { formatFileSize };

export const MAX_FILE_BYTES = 50 * 1024 * 1024;
export const ACCEPT = 'image/gif,.gif';

const PRESETS = {
    light: { maxColors: 200, scale: 1, frameSkip: 1 },
    balanced: { maxColors: 128, scale: 0.85, frameSkip: 1 },
    strong: { maxColors: 64, scale: 0.65, frameSkip: 2 },
};

export const PRESET_IDS = Object.keys(PRESETS);

export function isSupportedGifFile(file) {
    if (!file) return false;
    if (file.type === 'image/gif') return true;
    return /\.gif$/i.test(file.name || '');
}

export function compressedGifName(file) {
    const base = file.name.replace(/\.[^.]+$/, '');
    return `${base}-compressed.gif`;
}

function resizeRgba(rgba, srcW, srcH, dstW, dstH) {
    const srcCanvas = document.createElement('canvas');
    srcCanvas.width = srcW;
    srcCanvas.height = srcH;
    const srcCtx = srcCanvas.getContext('2d');
    srcCtx.putImageData(new ImageData(new Uint8ClampedArray(rgba), srcW, srcH), 0, 0);

    const dstCanvas = document.createElement('canvas');
    dstCanvas.width = dstW;
    dstCanvas.height = dstH;
    const dstCtx = dstCanvas.getContext('2d');
    dstCtx.drawImage(srcCanvas, 0, 0, dstW, dstH);
    return dstCtx.getImageData(0, 0, dstW, dstH).data;
}

function buildCompositedFrames(rawFrames, width, height) {
    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext('2d', { willReadFrequently: true });
    const composited = [];
    let previous = null;

    for (const frame of rawFrames) {
        if (frame.disposalType === 3 && previous) {
            ctx.putImageData(previous, 0, 0);
        }

        const saved = ctx.getImageData(0, 0, width, height);
        const frameImage = new ImageData(
            new Uint8ClampedArray(frame.patch),
            frame.dims.width,
            frame.dims.height,
        );
        ctx.putImageData(frameImage, frame.dims.left, frame.dims.top);

        composited.push({
            rgba: new Uint8ClampedArray(ctx.getImageData(0, 0, width, height).data),
            delay: frame.delay ?? 100,
            transparentIndex: frame.transparentIndex,
        });

        if (frame.disposalType === 2) {
            ctx.clearRect(frame.dims.left, frame.dims.top, frame.dims.width, frame.dims.height);
        }

        previous = frame.disposalType === 3 ? saved : null;
    }

    return composited;
}

function findTransparentPaletteIndex(palette) {
    for (let i = 0; i < palette.length; i += 1) {
        const color = palette[i];
        if (color.length === 4 && color[3] === 0) return i;
    }
    return 0;
}

/**
 * Compress an animated or static GIF in the browser.
 */
export async function compressGifFile(file, options, onProgress) {
    const preset = PRESETS[options.preset] || PRESETS.balanced;
    const buffer = await file.arrayBuffer();
    const parsed = parseGIF(buffer);
    const rawFrames = decompressFrames(parsed, true);

    if (!rawFrames.length) {
        throw new Error('no-frames');
    }

    const srcW = parsed.lsd.width;
    const srcH = parsed.lsd.height;
    const dstW = Math.max(1, Math.round(srcW * preset.scale));
    const dstH = Math.max(1, Math.round(srcH * preset.scale));
    const composited = buildCompositedFrames(rawFrames, srcW, srcH);
    const selected = composited.filter((_, index) => index % preset.frameSkip === 0);
    const hasTransparency = rawFrames.some((frame) => frame.transparentIndex != null);
    const quantFormat = hasTransparency ? 'rgba4444' : 'rgb565';
    const quantOptions = hasTransparency
        ? { format: 'rgba4444', oneBitAlpha: true }
        : { format: 'rgb565' };

    const encoder = GIFEncoder();

    for (let i = 0; i < selected.length; i += 1) {
        const frame = selected[i];
        const rgba = (dstW === srcW && dstH === srcH)
            ? frame.rgba
            : resizeRgba(frame.rgba, srcW, srcH, dstW, dstH);

        const palette = quantize(rgba, preset.maxColors, quantOptions);
        const indices = applyPalette(rgba, palette, quantFormat);
        const delay = Math.max(20, Math.round((frame.delay || 100) * preset.frameSkip));

        const frameOpts = {
            palette,
            delay,
        };

        if (hasTransparency) {
            frameOpts.transparent = true;
            frameOpts.transparentIndex = findTransparentPaletteIndex(palette);
        }

        encoder.writeFrame(indices, dstW, dstH, frameOpts);
        onProgress?.({
            frame: i + 1,
            frames: selected.length,
        });
    }

    encoder.finish();
    const bytes = encoder.bytes();
    const blob = new Blob([bytes], { type: 'image/gif' });

    return {
        name: compressedGifName(file),
        blob,
        originalSize: file.size,
        compressedSize: blob.size,
        frameCount: selected.length,
        width: dstW,
        height: dstH,
    };
}
